import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Award, ChevronDown, ClipboardList, Crosshair, ExternalLink, Gauge, Import, Landmark, Layers, LogOut, Menu, Settings, Target, Trophy, UserRound, Users, X } from "lucide-react";
import { BrandMark } from "../components/Logo";
import UnitSwitcher from "../components/UnitSwitcher";
import { ExpiryBanner } from "../components/ExpiryPill";
import { useApi } from "../api/client";
import { AccessSummary } from "../data/types";
import { adminSession } from "../api/client";

/*
 * `master` marks a page only the Association may use — the class register,
 * target definitions, club logins and system settings. A club login does not
 * see them, and the API refuses them regardless of what the menu shows.
 */
const navItems = [
  { to: "/admin", label: "Dashboard", icon: Gauge, end: true },
  { to: "/admin/competitions", label: "Events", icon: Trophy },
  // Event holds competitions, a competition holds matches — the menu follows.
  { to: "/admin/competition-list", label: "Competitions", icon: Award },
  { to: "/admin/matches", label: "Matches", icon: Crosshair },
  // A club defines the trophies for its own meetings, so this is not master-only.
  { to: "/admin/aggregates", label: "Aggregates", icon: Award },
  { to: "/admin/shot-logs", label: "Shooter Strings", icon: ClipboardList },
  { to: "/admin/targets", label: "Targets", icon: Target, master: true },
  { to: "/admin/competitors", label: "Shooters", icon: Users },
  { to: "/admin/classes", label: "Classes & Grades", icon: Layers, master: true },
  { to: "/admin/import", label: "Import / Export", icon: Import },
  { to: "/admin/clubs", label: "Clubs", icon: Landmark, master: true },
  { to: "/admin/users", label: "System Users", icon: UserRound, master: true },
  { to: "/admin/settings", label: "Settings", icon: Settings, master: true },
  { to: "/admin/audit-log", label: "Audit Log", icon: ClipboardList },
];

export function AdminTopbar({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-lg font-bold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="mt-0.5 text-[13px] text-muted">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
  );
}

/*
 * Account menu in the top-right corner. Each entry goes somewhere real —
 * the previous version was a static chevron that did nothing.
 */
function AccountMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = adminSession.user();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const signOut = () => {
    adminSession.clear();
    navigate("/admin/login", { replace: true });
  };

  const initials = (user?.name || user?.username || "A")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-3 rounded-lg px-1.5 py-1 transition-colors hover:bg-slate-100"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-sm font-bold text-slate-500">
          {initials}
        </span>
        <span className="hidden text-right sm:block">
          <span className="block text-sm font-semibold leading-tight text-ink">{user?.name || user?.username || "Admin"}</span>
          <span className="block text-xs text-muted">{user?.role || "Administrator"}</span>
        </span>
        <ChevronDown size={15} className={`text-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div role="menu" className="absolute right-0 top-full z-50 mt-1.5 w-60 overflow-hidden rounded-xl border border-line bg-white shadow-modal">
          <div className="border-b border-line px-4 py-3">
            <div className="text-sm font-bold text-ink">{user?.name || user?.username}</div>
            <div className="truncate text-xs text-muted">{user?.email || "—"}</div>
          </div>
          {[
            { label: "System Users", to: "/admin/users", icon: UserRound },
            { label: "Settings", to: "/admin/settings", icon: Settings },
            { label: "Audit Log", to: "/admin/audit-log", icon: ClipboardList },
          ].map(({ label, to, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-ink"
            >
              <Icon size={15} /> {label}
            </Link>
          ))}
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 border-t border-line px-4 py-2.5 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-ink"
          >
            <ExternalLink size={15} /> View Public Site
          </Link>
          <button
            onClick={signOut}
            className="flex w-full items-center gap-2.5 border-t border-line px-4 py-2.5 text-left text-[13px] font-semibold text-danger transition-colors hover:bg-danger-soft"
          >
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = adminSession.user();
  // Below lg the sidebar becomes an off-canvas drawer opened from the top bar.
  const [navOpen, setNavOpen] = useState(false);
  /*
   * What this login may do, asked of the API rather than read from the token —
   * the Association can change a club's expiry while it is signed in, and the
   * menu should follow without a re-login. Until it answers, the master menu is
   * shown: hiding pages from the Association for a moment is the lesser fault,
   * and the API refuses anything a club is not entitled to regardless.
   */
  const { data: access } = useApi<AccessSummary | null>("/admin/me", null);
  const visibleNav = navItems.filter((n) => !n.master || access?.isMaster !== false);

  // Any navigation closes the drawer, otherwise it covers the page you just opened.
  useEffect(() => setNavOpen(false), [location.pathname]);

  return (
    <div className="min-h-screen bg-page">
      {/* Scrim behind the drawer on small screens */}
      {navOpen && (
        <div
          className="fixed inset-0 z-40 bg-navy-950/50 backdrop-blur-sm lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[240px] flex-col bg-navy-950 transition-transform duration-200 lg:w-[208px] lg:translate-x-0 ${
          navOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-4 py-3.5">
          <Link to="/admin">
            <BrandMark compact />
          </Link>
          <button
            onClick={() => setNavOpen(false)}
            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-navy-800 hover:text-white lg:hidden"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Admin Panel</div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2.5 pb-3">
          {visibleNav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors ${
                  isActive ? "bg-primary text-white" : "text-slate-300 hover:bg-navy-800 hover:text-white"
                }`
              }
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-navy-800 px-4 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-500 text-sm font-bold text-white">
              {(user?.name || user?.username || "A").charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white">{user?.name || user?.username || "Admin"}</div>
              <div className="truncate text-xs text-slate-400">{user?.role || "Administrator"}</div>
            </div>
          </div>
          <button
            onClick={() => {
              adminSession.clear();
              navigate("/admin/login");
            }}
            className="mt-4 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-navy-800 hover:text-white"
          >
            <LogOut size={17} />
            Logout
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-col lg:ml-[208px]">
        {/* The notification bell and help button were removed — neither had
            anything behind it. The account menu below is functional. */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-line bg-white/90 px-4 backdrop-blur sm:px-5">
          <button
            onClick={() => setNavOpen(true)}
            className="-ml-1 rounded-lg p-2 text-muted transition-colors hover:bg-slate-100 hover:text-ink lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <div className="ml-auto flex items-center gap-2">
            <UnitSwitcher />
            <AccountMenu />
          </div>
        </header>
        <main className="min-w-0 flex-1 px-4 py-4 sm:px-5">
          {/* The renewal countdown, silent until a warning mark is reached. */}
          <ExpiryBanner expiry={access?.expiry} club={access?.club} className="mb-4" />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
